import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface CardsUpdateContextType {
  subscribe: (callback: () => void) => () => void;
  notify: () => void;
  isUpdating: boolean;
  setIsUpdating: (updating: boolean) => void;
}

const CardsUpdateContext = createContext<CardsUpdateContextType | undefined>(undefined);

export const useCardsUpdate = () => {
  const context = useContext(CardsUpdateContext);
  if (!context) {
    throw new Error('useCardsUpdate must be used within a CardsUpdateProvider');
  }
  return context;
};

interface CardsUpdateProviderProps {
  children: ReactNode;
}

export const CardsUpdateProvider: React.FC<CardsUpdateProviderProps> = ({ children }) => {
  const [subscribers, setSubscribers] = useState<Set<() => void>>(new Set());
  const [isUpdating, setIsUpdating] = useState(false);

  const subscribe = useCallback((callback: () => void) => {
    setSubscribers(prev => new Set(prev).add(callback));

    // Return unsubscribe function
    return () => {
      setSubscribers(prev => {
        const newSet = new Set(prev);
        newSet.delete(callback);
        return newSet;
      });
    };
  }, []);

  const notify = useCallback(() => {
    setIsUpdating(true);

    // Notify all subscribers
    subscribers.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in cards update subscriber:', error);
      }
    });

    // Reset updating state after a short delay
    setTimeout(() => {
      setIsUpdating(false);
    }, 1000);
  }, [subscribers]);

  const value: CardsUpdateContextType = {
    subscribe,
    notify,
    isUpdating,
    setIsUpdating
  };

  return (
    <CardsUpdateContext.Provider value={value}>
      {children}
    </CardsUpdateContext.Provider>
  );
};
